import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../utils/api-config';
import { FaInfoCircle, FaRegClock } from 'react-icons/fa';

const ChatDetail = ({ channel }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const fetchMessages = useCallback(async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${ENDPOINTS.SENDBIRD.CHANNELS}/${channel.channel_url}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newMessages = (response.data.data?.messages || []).sort((a, b) => a.created_at - b.created_at);
      setMessages(newMessages);
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [channel.channel_url]);

  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  useEffect(() => {
    fetchMessages(true);
    setShouldAutoScroll(true); // Reset auto-scroll when channel changes
    const interval = setInterval(() => fetchMessages(false), 3000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    // If user is within 100px of bottom, keep auto-scroll enabled
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
    setShouldAutoScroll(isAtBottom);
  };

  useEffect(() => {
    if (shouldAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, shouldAutoScroll]);

  const formatTime = (ts) => {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getDisplayName = (ch) => {
    const customer = ch.members?.find(m => m.role === 'customer');
    const provider = ch.members?.find(m => m.role === 'provider');
    if (customer && provider) return `${customer.nickname} ↔ ${provider.nickname}`;
    
    const names = new Set();
    ch.members?.forEach(m => { if (m.nickname) names.add(m.nickname); });
    if (ch.created_by?.nickname) {
      names.add(ch.created_by.nickname);
    }
    if (ch.last_message?.user?.nickname) {
      names.add(ch.last_message.user.nickname);
    }
    if (names.size < 2 && ch.name && ch.name !== 'Group Channel') {
      ch.name.split(',').forEach(n => { if (n.trim()) names.add(n.trim()); });
    }
    return Array.from(names).join(', ') || 'Unknown Chat';
  };

  const displayName = getDisplayName(channel);

  return (
    <div className="chat-detail-container">
      <div className="chat-header">
        <div className="chat-user-info">
          <div>
            <h3 className="chat-header-names">
              {channel.members && channel.members.length > 1 ? (
                channel.members.map((member, idx) => (
                  <span key={member.user_id || idx} className="member-name-wrapper">
                    <img 
                      src={member.profile_url || 'https://static.sendbird.com/sample/cover/cover_15.jpg'} 
                      alt={member.nickname} 
                      className="avatar-inline header-avatar" 
                      onError={(e) => { e.target.src = 'https://static.sendbird.com/sample/cover/cover_15.jpg'; }}
                    />
                    <span className="member-nickname">{member.nickname || 'Unknown'}</span>
                    {idx < channel.members.length - 1 && <span className="channel-separator">,&nbsp;</span>}
                  </span>
                ))
              ) : (
                displayName
              )}
            </h3>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Conversation History
            </span>
          </div>
        </div>
        <div className="chat-actions">
          <div className="channel-meta" style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            <FaInfoCircle /> ID: {channel.channel_url.substring(0, 15)}...
          </div>
        </div>
      </div>

      <div className="messages-viewport" onScroll={handleScroll}>
        {loading && messages.length === 0 ? (
          <div className="no-selection">
            <div className="spinner-small"></div>
            <p>Loading history...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="no-selection">
            <p>No messages yet in this conversation.</p>
          </div>
        ) : (
          messages.map((msg, index) => {
            // Determine alignment by simply comparing to the first member in the channel
            // since 'role' might not be available in the Sendbird member object
            const rightUserId = channel.members[0]?.user_id;
            const isRight = msg.user?.user_id === rightUserId;
            
            // Resolve the enriched nickname from channel members
            const memberInfo = channel.members.find(m => m.user_id === msg.user?.user_id);
            const displayName = memberInfo?.nickname || msg.user?.nickname || 'Unknown';
            
            return (
              <div key={msg.message_id || index} className={`message-row ${isRight ? 'right' : 'left'}`}>
                <div className="message-bubble">
                  <div className="message-sender">
                    {/* {msg.user?.nickname || 'Unknown'} */}
                    {displayName}
                  </div>
                  <div className="message-text">
                    {msg.message}
                  </div>
                  <div className="message-time">
                    {formatTime(msg.created_at)}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default ChatDetail;
